import { useEffect, useState } from 'react'
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom'
import { useAccount, useSignMessage } from 'wagmi'
import { useAppKit } from '@reown/appkit/react'
import {
  LayoutDashboard,
  ListOrdered,
  Settings,
  FileText,
  ArrowLeft,
  Wallet,
  LogIn,
  Loader2,
  Users,
  Landmark,
  FileStack,
  ShieldX,
  Sun,
  Moon,
  Sliders,
  Menu,
  X,
} from 'lucide-react'
import { cn, shortenAddress } from '@/lib/utils'
import { useAuthStore } from '@/store/useAuthStore'
import { useTheme } from '@/contexts/ThemeContext'
import { MobileDrawer } from './MobileDrawer'

export function AdminLayout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const location = useLocation()
  const { address, isConnected } = useAccount()
  const { open } = useAppKit()
  const { signMessageAsync } = useSignMessage()
  const { theme, toggleTheme } = useTheme()

  const {
    user,
    isAuthenticated,
    isAdmin,
    isAdminCandidate,
    adminCandidateChecked,
    isLoading,
    signIn,
    checkAuth,
    refreshAdminCandidate,
  } = useAuthStore()

  // Re-validate JWT-vs-wallet AND ask backend if this address is admin
  // whenever the connected wallet changes.
  useEffect(() => {
    checkAuth(address)
    refreshAdminCandidate(address)
  }, [address])

  // Close the admin mobile drawer on route change so the panel doesn't
  // linger over the new page after the user taps a nav item.
  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  if (!isConnected) {
    return <Navigate to="/" replace />
  }

  // While the /auth/is-admin probe is in flight, show a neutral spinner so
  // we don't flash "Acceso Denegado" at legit admins before the response.
  if (!adminCandidateChecked) {
    return (
      <div className={cn(
        'min-h-screen flex items-center justify-center p-4',
        theme === 'dark' ? 'bg-dark-950' : 'bg-gray-50'
      )}>
        <Loader2 className={cn(
          'w-8 h-8 animate-spin',
          theme === 'dark' ? 'text-dark-400' : 'text-gray-400'
        )} />
      </div>
    )
  }

  // Backend gate: if this address is NOT in the walletHolder table, deny
  // before any SIWE attempt. No hardcoded list anywhere.
  if (!isAdminCandidate) {
    return (
      <div className={cn(
        'min-h-screen flex items-center justify-center p-4',
        theme === 'dark' ? 'bg-dark-950' : 'bg-gray-50'
      )}>
        <div className={cn(
          'rounded-2xl border p-8 max-w-md w-full text-center space-y-6',
          theme === 'dark'
            ? 'bg-dark-900 border-red-500/30'
            : 'bg-white border-red-300'
        )}>
          <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto">
            <ShieldX className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h1 className={cn(
              'text-xl font-semibold mb-2',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>Acceso Denegado</h1>
            <p className={cn(
              'text-sm',
              theme === 'dark' ? 'text-dark-400' : 'text-gray-500'
            )}>
              Esta wallet no tiene permisos para acceder al panel de administración.
            </p>
          </div>
          <div className={cn(
            'rounded-lg p-4 text-sm',
            theme === 'dark' ? 'bg-dark-800/50' : 'bg-gray-100'
          )}>
            <p className={cn(
              'mb-1',
              theme === 'dark' ? 'text-dark-500' : 'text-gray-400'
            )}>Wallet conectada:</p>
            <p className={cn(
              'font-mono',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>{shortenAddress(address || '')}</p>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-red-400 hover:text-red-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </div>
    )
  }

  const handleSignIn = async () => {
    if (!address) return
    await signIn(address, signMessageAsync)
  }

  const navItems = [
    { path: '/admin/dashboard', label: 'Panel', icon: LayoutDashboard },
    { path: '/admin/queue', label: 'Cola', icon: ListOrdered },
    { path: '/admin/wallets', label: 'Wallets', icon: Landmark },
    { path: '/admin/config', label: 'Configuración', icon: Settings },
    { path: '/admin/p2p-contract', label: 'P2P Contrato', icon: Sliders },
    { path: '/admin/templates', label: 'Plantillas', icon: FileStack },
    { path: '/admin/audit', label: 'Auditoría', icon: FileText },
    { path: '/admin/holders', label: 'Holders', icon: Users },
  ]

  // SEGURIDAD: Verificar que además de autenticado, sea admin en la BD
  // Show sign-in screen if not authenticated
  if (!isAuthenticated) {
    console.log('[AdminLayout] Usuario no autenticado, mostrando pantalla de login')
    return (
      <div className={cn(
        'min-h-screen flex items-center justify-center p-4',
        theme === 'dark' ? 'bg-dark-950' : 'bg-gray-50'
      )}>
        <div className={cn(
          'rounded-2xl border p-8 max-w-md w-full text-center space-y-6',
          theme === 'dark'
            ? 'bg-dark-900 border-dark-700'
            : 'bg-white border-gray-200'
        )}>
          <div className="w-16 h-16 bg-uni-500/20 rounded-2xl flex items-center justify-center mx-auto">
            <LogIn className="w-8 h-8 text-uni-400" />
          </div>
          <div>
            <h1 className={cn(
              'text-xl font-semibold mb-2',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>Panel de Administración</h1>
            <p className={cn(
              'text-sm',
              theme === 'dark' ? 'text-dark-400' : 'text-gray-500'
            )}>
              Para acceder al panel de administración, necesitas firmar un mensaje con tu wallet para verificar tu identidad.
            </p>
          </div>
          <div className={cn(
            'rounded-lg p-4 text-sm',
            theme === 'dark' ? 'bg-dark-800/50' : 'bg-gray-100'
          )}>
            <p className={cn(
              'mb-1',
              theme === 'dark' ? 'text-dark-500' : 'text-gray-400'
            )}>Wallet conectada:</p>
            <p className={cn(
              'font-mono',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>{shortenAddress(address || '')}</p>
          </div>
          <button
            onClick={handleSignIn}
            disabled={isLoading}
            className="btn-primary btn-lg w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Verificando...
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                Iniciar Sesión
              </>
            )}
          </button>
          <Link
            to="/"
            className={cn(
              'inline-flex items-center gap-2 text-sm transition-colors',
              theme === 'dark' ? 'text-dark-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
            )}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al Bridge
          </Link>
        </div>
      </div>
    )
  }

  // SEGURIDAD: Usuario autenticado pero NO es admin en la BD
  if (!isAdmin) {
    console.warn('[AdminLayout] ACCESO DENEGADO - Usuario autenticado pero no es admin en BD:', {
      address,
      user,
      isAdmin,
    })
    return (
      <div className={cn(
        'min-h-screen flex items-center justify-center p-4',
        theme === 'dark' ? 'bg-dark-950' : 'bg-gray-50'
      )}>
        <div className={cn(
          'rounded-2xl border p-8 max-w-md w-full text-center space-y-6',
          theme === 'dark'
            ? 'bg-dark-900 border-orange-500/30'
            : 'bg-white border-orange-300'
        )}>
          <div className="w-16 h-16 bg-orange-500/20 rounded-2xl flex items-center justify-center mx-auto">
            <ShieldX className="w-8 h-8 text-orange-400" />
          </div>
          <div>
            <h1 className={cn(
              'text-xl font-semibold mb-2',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>Sin Permisos de Admin</h1>
            <p className={cn(
              'text-sm',
              theme === 'dark' ? 'text-dark-400' : 'text-gray-500'
            )}>
              Tu wallet no tiene permisos de administrador configurados en el sistema.
            </p>
          </div>
          <div className={cn(
            'rounded-lg p-4 text-sm',
            theme === 'dark' ? 'bg-dark-800/50' : 'bg-gray-100'
          )}>
            <p className={cn(
              'mb-1',
              theme === 'dark' ? 'text-dark-500' : 'text-gray-400'
            )}>Wallet:</p>
            <p className={cn(
              'font-mono',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>{shortenAddress(address || '')}</p>
          </div>
          <p className={cn(
            'text-xs',
            theme === 'dark' ? 'text-dark-500' : 'text-gray-400'
          )}>
            Contacta con un administrador para que te asigne los permisos necesarios.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-orange-400 hover:text-orange-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </div>
    )
  }

  console.log('[AdminLayout] ACCESO PERMITIDO - Usuario admin verificado')

  // Sidebar body — rendered both inside the permanent desktop sidebar and
  // inside the mobile drawer to keep parity.
  const sidebarBody = (
    <>
      <div className={cn(
        'h-14 px-4 flex items-center border-b shrink-0',
        theme === 'dark' ? 'border-dark-800' : 'border-gray-200'
      )}>
        <Link to="/" className={cn(
          'flex items-center gap-2 text-sm transition-colors',
          theme === 'dark' ? 'text-dark-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
        )}>
          <ArrowLeft className="w-4 h-4" />
          Volver al Bridge
        </Link>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <p className={cn(
          'text-xs font-semibold uppercase tracking-wider mb-2 px-3',
          theme === 'dark' ? 'text-dark-500' : 'text-gray-400'
        )}>
          Administración
        </p>
        {navItems.map((item) => {
          const Icon = item.icon
          const isActive = location.pathname === item.path
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm',
                isActive
                  ? 'bg-uni-600/20 text-uni-400'
                  : theme === 'dark'
                    ? 'text-dark-400 hover:text-white hover:bg-dark-800'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              )}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {item.label}
            </Link>
          )
        })}
      </nav>

      <div className={cn(
        'p-3 border-t shrink-0',
        theme === 'dark' ? 'border-dark-800' : 'border-gray-200'
      )}>
        <button
          onClick={() => open()}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm',
            theme === 'dark'
              ? 'bg-dark-800 hover:bg-dark-700'
              : 'bg-gray-100 hover:bg-gray-200'
          )}
        >
          <Wallet className={cn(
            'w-4 h-4 shrink-0',
            theme === 'dark' ? 'text-dark-400' : 'text-gray-400'
          )} />
          <span className={cn(
            'font-mono truncate text-xs',
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            {shortenAddress(address || '')}
          </span>
        </button>
      </div>
    </>
  )

  return (
    <div className={cn(
      'min-h-screen flex',
      theme === 'dark' ? 'bg-dark-950' : 'bg-gray-100'
    )}>
      {/* Permanent sidebar — desktop only */}
      <aside className={cn(
        'hidden lg:flex w-56 border-r flex-col',
        theme === 'dark'
          ? 'bg-dark-900 border-dark-800'
          : 'bg-white border-gray-200'
      )}>
        {sidebarBody}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className={cn(
          'h-14 px-3 sm:px-4 lg:px-6 flex items-center justify-between gap-2 border-b shrink-0',
          theme === 'dark'
            ? 'border-dark-800 bg-dark-900/50'
            : 'border-gray-200 bg-white/50'
        )}>
          {/* Mobile: hamburger + compact title; desktop: full title only */}
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setMobileNavOpen(true)}
              aria-label="Abrir menú admin"
              className={cn(
                'lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0',
                theme === 'dark'
                  ? 'bg-dark-800/50 text-dark-300 hover:text-white'
                  : 'bg-gray-100 text-gray-600 hover:text-gray-900',
              )}
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className={cn(
              'font-semibold truncate text-base lg:text-lg',
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            )}>
              <span className="hidden sm:inline">Panel de Administración</span>
              <span className="sm:hidden">Admin</span>
            </h1>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Theme Toggle — always visible (frequent toggle) */}
            <button
              onClick={toggleTheme}
              className={cn(
                'inline-flex items-center justify-center w-10 h-10 rounded-lg transition-all',
                theme === 'dark'
                  ? 'bg-dark-800 border border-dark-700 hover:border-dark-600 text-dark-300 hover:text-white'
                  : 'bg-white border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900'
              )}
              title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {user && (
              <span className={cn(
                'hidden md:inline text-xs',
                theme === 'dark' ? 'text-dark-500' : 'text-gray-400'
              )}>
                Sesión activa
              </span>
            )}
            {/* Wallet pill — hidden on small mobile (it lives in the drawer) */}
            <button
              onClick={() => open()}
              className={cn(
                'hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm border',
                theme === 'dark'
                  ? 'bg-dark-800 hover:bg-dark-700 border-dark-700'
                  : 'bg-white hover:bg-gray-50 border-gray-200'
              )}
            >
              <Wallet className="w-4 h-4 text-uni-400" />
              <span className={cn(
                'font-mono text-xs',
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                {shortenAddress(address || '')}
              </span>
            </button>
          </div>
        </header>

        <main className="flex-1 p-3 sm:p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile drawer mirrors the desktop sidebar */}
      <MobileDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        side="left"
        panelClassName={cn(
          'flex flex-col',
          theme === 'dark'
            ? 'bg-dark-900 border-dark-800'
            : 'bg-white border-gray-200',
        )}
      >
        {/* Drawer header — only visible inside the mobile panel */}
        <div className={cn(
          'flex items-center justify-between h-14 px-4 border-b shrink-0',
          theme === 'dark' ? 'border-dark-800' : 'border-gray-200',
        )}>
          <span className={cn(
            'font-semibold text-sm',
            theme === 'dark' ? 'text-white' : 'text-gray-900',
          )}>
            Panel admin
          </span>
          <button
            onClick={() => setMobileNavOpen(false)}
            aria-label="Cerrar menú"
            className={cn(
              'inline-flex items-center justify-center w-10 h-10 rounded-lg',
              theme === 'dark'
                ? 'text-dark-300 hover:text-white hover:bg-dark-800/50'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100',
            )}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {sidebarBody}
      </MobileDrawer>
    </div>
  )
}
